/**
 * Filter state in the URL, one codec per filter kind (doc 05-admin/04 §3.3; doc 01 §5.2).
 *
 * A queue's tab, search and filters are search params, so a pasted link reopens the view the
 * sender saw and the back button undoes a filter. Each kind decodes what the router hands it —
 * a string, or the number or boolean the router's JSON parsing made of one — and anything it
 * cannot read degrades to "no filter". Nothing here throws: a mangled link is a default view,
 * never a crashed screen.
 *
 * `namespace` keeps two tables on one page apart: their keys are `{namespace}_{name}`.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

export type FilterDef =
  | { kind: 'search' }
  | { kind: 'select'; options: readonly string[] }
  | { kind: 'multiselect'; options: readonly string[] }
  | { kind: 'daterange' }
  | { kind: 'boolean' }
  | { kind: 'id' };

export interface DateRange {
  from: string;
  to: string;
}

export type FilterValue<D extends FilterDef> = D extends { kind: 'search' | 'select' }
  ? string
  : D extends { kind: 'multiselect' }
    ? string[]
    : D extends { kind: 'daterange' }
      ? DateRange
      : D extends { kind: 'boolean' }
        ? boolean
        : number;

export type FilterValues<Defs extends Record<string, FilterDef>> = {
  [K in keyof Defs]?: FilterValue<Defs[K]>;
};

/** Long enough for any real query, short enough that a pasted essay is not sent to the server. */
const SEARCH_MAX = 200;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function text(raw: unknown): string | undefined {
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  return typeof raw === 'string' ? raw : undefined;
}

function isoDate(value: string): boolean {
  /* `2026-02-30` matches the pattern and is not a day; the round trip catches it. */
  return ISO_DATE.test(value) && new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);
}

/** The value a param carries, or undefined when it carries nothing this kind can use. */
export function decodeFilter<D extends FilterDef>(def: D, raw: unknown): FilterValue<D> | undefined {
  const s = text(raw);
  if (s === undefined) return undefined;
  switch (def.kind) {
    case 'search': {
      const q = s.trim().slice(0, SEARCH_MAX);
      return (q || undefined) as FilterValue<D> | undefined;
    }
    case 'select':
      return (def.options.includes(s) ? s : undefined) as FilterValue<D> | undefined;
    case 'multiselect': {
      const picked = new Set(s.split(','));
      /* The options' order, not the URL's: two links naming the same set read the same. */
      const values = def.options.filter((o) => picked.has(o));
      return (values.length ? values : undefined) as FilterValue<D> | undefined;
    }
    case 'daterange': {
      const [from, to, extra] = s.split('..');
      if (extra !== undefined || !from || !to || !isoDate(from) || !isoDate(to) || from > to) return undefined;
      return { from, to } as FilterValue<D>;
    }
    case 'boolean':
      return (s === 'true' ? true : s === 'false' ? false : undefined) as FilterValue<D> | undefined;
    case 'id':
      return (/^[1-9]\d{0,15}$/.test(s) ? Number(s) : undefined) as FilterValue<D> | undefined;
  }
}

/** The param to write for a value; undefined removes the param. */
export function encodeFilter<D extends FilterDef>(def: D, value: FilterValue<D> | undefined): string | undefined {
  if (value === undefined) return undefined;
  switch (def.kind) {
    case 'multiselect': {
      const values = value as string[];
      return values.length ? def.options.filter((o) => values.includes(o)).join(',') : undefined;
    }
    case 'daterange': {
      const { from, to } = value as DateRange;
      return `${from}..${to}`;
    }
    case 'search':
      return (value as string).trim() || undefined;
    default:
      return String(value);
  }
}

export function filterKey(namespace: string, name: string): string {
  return `${namespace}_${name}`;
}

/** Every filter of a table, read from a search object. Absent keys stay absent. */
export function decodeFilters<Defs extends Record<string, FilterDef>>(
  namespace: string,
  defs: Defs,
  search: Record<string, unknown>,
): FilterValues<Defs> {
  const out: FilterValues<Defs> = {};
  for (const name of Object.keys(defs) as (keyof Defs & string)[]) {
    const value = decodeFilter(defs[name]!, search[filterKey(namespace, name)]);
    if (value !== undefined) out[name] = value;
  }
  return out;
}

/** The next search object: `patch` applied over `prev`, each key encoded or removed. */
export function applyFilters<Defs extends Record<string, FilterDef>>(
  namespace: string,
  defs: Defs,
  prev: Record<string, unknown>,
  patch: FilterValues<Defs>,
): Record<string, unknown> {
  const next = { ...prev };
  for (const name of Object.keys(patch) as (keyof Defs & string)[]) {
    const key = filterKey(namespace, name);
    const encoded = encodeFilter(defs[name]!, patch[name]);
    if (encoded === undefined) delete next[key];
    else next[key] = encoded;
  }
  return next;
}

/**
 * A table's filters and a setter. A filter change is a history entry — back undoes it — unlike
 * the analytics window, whose preset flicking replaces one.
 */
export function useUrlFilters<Defs extends Record<string, FilterDef>>(
  namespace: string,
  defs: Defs,
): [FilterValues<Defs>, (patch: FilterValues<Defs>) => void] {
  const values = decodeFilters(namespace, defs, useSearch({ strict: false }) as Record<string, unknown>);
  const navigate = useNavigate();
  const set = useCallback(
    (patch: FilterValues<Defs>) =>
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => applyFilters(namespace, defs, prev, patch),
      } as never),
    [navigate, namespace, defs],
  );
  return [values, set];
}

/**
 * A queue's tab in the URL as `?{namespace}_tab=`. The default tab writes no param, so the bare
 * path stays the canonical link to it; a tab the queue does not have opens the default.
 * `keys` must be stable (a module-level constant), since it keys the codec.
 */
export function useUrlTab(namespace: string, keys: readonly string[], fallback: string): [string, (key: string) => void] {
  const defs = useMemo(() => ({ tab: { kind: 'select', options: keys } }) as const, [keys]);
  const [values, set] = useUrlFilters(namespace, defs);
  const setTab = useCallback((key: string) => set({ tab: key === fallback ? undefined : key }), [set, fallback]);
  return [values.tab ?? fallback, setTab];
}

/**
 * A queue's search box in the URL as `?{namespace}_{name}=` (`name` defaults to `q`). The box
 * keeps its own text — the codec trims, and a box that re-read the URL on every keystroke would
 * eat the space typed between two words — and mirrors the trimmed query into the URL, replacing
 * the history entry, so a search is not a back-button stop per letter. A pasted link opens with
 * its query already typed.
 */
export function useUrlSearch(namespace: string, name = 'q'): [string, (value: string) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const defs = useMemo(() => ({ [name]: { kind: 'search' } }) as Record<string, FilterDef>, [name]);
  const [q, setLocal] = useState(() => (decodeFilter(defs[name]!, search[filterKey(namespace, name)]) as string | undefined) ?? '');
  const navigate = useNavigate();
  const setQ = useCallback(
    (value: string) => {
      setLocal(value);
      void navigate({
        to: '.',
        replace: true,
        search: (prev: Record<string, unknown>) => applyFilters(namespace, defs, prev, { [name]: value }),
      } as never);
    },
    [navigate, namespace, name, defs],
  );
  return [q, setQ];
}
